/**
 * VendorDetailService — global vendor detail panel state.
 *
 * RESPONSIBILITY: Controls the vendor detail slide-in panel visibility and loads vendor data.
 * Can be opened from anywhere in the app via open(vendorId) — Command-K, order detail, home widget.
 * Does NOT manage ordering or cart — only vendor info display.
 *
 * SIGNALS:
 *   visible (readonly)   — panel open/closed
 *   vendor (readonly)    — loaded vendor data (CateringVendor | null)
 *   menu (readonly)      — loaded menu data for preview
 *   loading (readonly)   — loading state
 *
 * USED BY: VendorDetailPanelComponent, CommandPaletteComponent, OrderDetailComponent
 *
 * BACKEND CONTRACT: cateringVendor query, cateringMenu query via CateringApiService
 *
 * AI CONTEXT: This is a global singleton panel — only one vendor can be displayed at a time.
 * Opening a new vendor replaces the previous one. Menu is loaded in parallel with vendor
 * for the menu preview section.
 */
import { Injectable, inject, signal } from '@angular/core';
import { CateringApiService } from './catering-api.service';
import { CateringVendor, CateringMenu } from '../models/catering.model';

@Injectable({ providedIn: 'root' })
export class VendorDetailService {
  private readonly api = inject(CateringApiService);

  // ── Private signals ─────────────────────────────────

  private readonly _visible = signal(false);
  private readonly _vendor = signal<CateringVendor | null>(null);
  private readonly _menu = signal<CateringMenu[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  // ── Public readonly signals ─────────────────────────

  readonly visible = this._visible.asReadonly();
  readonly vendor = this._vendor.asReadonly();
  readonly menu = this._menu.asReadonly();
  readonly loading = this._loading.asReadonly();
  /** User-facing error from the last failed open(). Cleared on the next
   *  successful load or close(). The detail panel renders an inline notice
   *  so opening a vendor that's offline doesn't show empty state. */
  readonly error = this._error.asReadonly();

  // ── Methods ─────────────────────────────────────────

  async open(vendorId: string): Promise<void> {
    this._visible.set(true);
    this._loading.set(true);
    this._error.set(null);
    try {
      const [vendor, menu] = await Promise.all([
        this.api.loadVendor(vendorId),
        this.api.loadMenu(vendorId),
      ]);
      this._vendor.set(vendor);
      this._menu.set(menu);
    } catch (cause) {
      console.warn('[VendorDetail] load error:', cause);
      this._error.set(cause instanceof Error ? cause.message : 'Failed to load vendor');
    } finally {
      this._loading.set(false);
    }
  }

  close(): void {
    this._visible.set(false);
    this._vendor.set(null);
    this._menu.set([]);
    this._error.set(null);
  }
}
